import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  CheckCircle, 
  Users, 
  Star, 
  TrendingUp, 
  Clock, 
  Shield, 
  Wallet,
  ArrowRight,
  Phone,
  Award,
  Target,
  Zap
} from 'lucide-react';

const NedenBizeKatil = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Wallet,
      title: 'Kazandıkça Öde',
      description: 'Üyelik ücretsiz! Komisyon sadece işi tamamladığında. Sana iş gelmezse hiçbir ödeme yapmazsın.',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Users,
      title: 'Binlerce Müşteri',
      description: 'Her gün yüzlerce yeni servis talebi. Senin uzmanlık alanındaki müşteriler seni bekliyor.',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Clock,
      title: 'Esnek Çalışma',
      description: 'Kendi çalışma saatlerini sen belirle. İstediğin zaman, istediğin kadar çalış.',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: TrendingUp,
      title: 'Gelirini Artır',
      description: 'Ortalama ustalarımız ayda 15.000₺+ kazanıyor. Daha fazla iş, daha fazla kazanç.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Shield,
      title: 'Güvenli Ödeme',
      description: 'Tüm ödemeler güvenli sistem üzerinden. Paranız zamanında ve sorunsuz hesabınıza.',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      icon: Award,
      title: 'Profesyonel Destek',
      description: '7/24 usta destek hattı. Sorularında ve işlerinde her zaman yanındayız.',
      color: 'from-pink-500 to-rose-600'
    }
  ];

  const steps = [
    { step: 1, title: 'Ücretsiz Kayıt Ol', desc: 'Bilgilerini gir, uzmanlık alanını seç' },
    { step: 2, title: 'Profili Tamamla', desc: 'Deneyimini ve sertifikalarını ekle' },
    { step: 3, title: 'Onay Al', desc: 'Ekibimiz profilini incelesin' },
    { step: 4, title: 'İşlere Başla', desc: 'Müşterilerden talepler gelsin' }
  ];

  const testimonials = [
    {
      name: 'Ahmet K.',
      role: 'Kombi Ustası',
      location: 'İstanbul',
      text: '3 yıldır HizmetGeliyo ile çalışıyorum. Artık müşteri bulmak için uğraşmıyorum, işler bana geliyor.',
      avatar: 'A'
    },
    {
      name: 'Murat Y.',
      role: 'Klima Teknisyeni', 
      location: 'Ankara',
      text: 'Kendi patronum oldum. Esnek çalışma saatleri sayesinde ailemle daha fazla vakit geçiriyorum.',
      avatar: 'M'
    },
    {
      name: 'Hasan D.',
      role: 'Elektrik Ustası',
      location: 'İzmir',
      text: 'Gelirim %40 arttı. Platform sayesinde sürekli iş akışı var, boş kalmıyorum.',
      avatar: 'H'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/84fc7f27-88da-4cbc-8349-efcf97b1d474/images/52f675ee106fe12744da637f6b4758714d0dc9893774ca59b074b728036f42d7.png" 
            alt="Usta olarak katıl" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/90 via-[#0f172a]/70 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-32 pb-16 md:pt-40 md:pb-24">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#E29578]/20 backdrop-blur-sm border border-[#E29578]/30 rounded-full px-4 py-2 mb-6">
              <Target className="w-4 h-4 text-[#E29578]" />
              <span className="text-[#E29578] text-sm font-semibold">Usta Fırsatı</span>
            </div>

            {/* Main Slogan */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Siz Çalışın,<br />
              <span className="text-[#E29578]">Biz İş Getirelim;</span><br />
              <span className="text-[#F6AD55]">Kazandıkça Ödeyin.</span>
            </h1>

            <p className="text-white/80 text-lg md:text-xl mb-8 leading-relaxed">
              HizmetGeliyo ile binlerce müşteriye ulaş. Üyelik tamamen ücretsiz, 
              komisyon sadece başarılı işlerde. Hemen başvur, yarın işe başla!
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <div className="text-2xl md:text-3xl font-bold text-white">500+</div>
                <div className="text-white/70 text-sm">Aktif Usta</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <div className="text-2xl md:text-3xl font-bold text-white">25K+</div>
                <div className="text-white/70 text-sm">Tamamlanan İş</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <div className="text-2xl md:text-3xl font-bold text-white">4.9</div>
                <div className="text-white/70 text-sm">Usta Memnuniyeti</div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/usta-kayit')}
                className="bg-[#E29578] hover:bg-[#D67A56] text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
              >
                Hemen Başvur
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/prousta')}
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#006D77] px-8 py-4 rounded-full text-lg font-semibold transition-all"
              >
                Usta Girişi
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden HizmetGeliyo?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Türkiye'nin en hızlı büyüyen hizmet platformunda yerinizi alın
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Nasıl Başlarsın?
            </h2>
            <p className="text-gray-600 text-lg">
              4 kolay adımda HizmetGeliyo ailesine katıl
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-[#006D77] to-[#E29578]"></div>
                )}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg relative z-10">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#006D77] to-[#0A9396]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Ustalarımız Ne Diyor?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{testimonial.name}</h4>
                    <p className="text-white/70 text-sm">{testimonial.role} • {testimonial.location}</p>
                  </div>
                </div>
                <p className="text-white/90 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            {/* Decorative */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E29578]/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#006D77]/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10">
              <Zap className="w-12 h-12 text-[#F6AD55] mx-auto mb-4" />
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                Hemen Başla, Yarın Kazan
              </h2>
              <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
                Binlerce usta HizmetGeliyo ile kazanıyor. Sen de ailemize katıl, 
                müşteriler seni bulsun!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/usta-kayit')}
                  className="bg-[#E29578] hover:bg-[#D67A56] text-white px-8 py-4 rounded-full text-lg font-semibold"
                >
                  Ücretsiz Başvur
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a href="tel:08502427808" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all">
                  <Phone className="w-5 h-5" />
                  0850 242 78 08
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NedenBizeKatil;
